import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DEFAULT_PARTS_VIEW,
  isPartsView,
  type PartsView,
} from "../presentation.js";

/**
 * The Parts workspace keeps its whole visible state in the address:
 *
 * - `view`  — which list is shown (`work_queue` when absent or unknown);
 * - `case`  — the selected case, open in the inspector;
 * - `q`     — the search applied inside the view;
 * - `noEta` — only cases with a line that has no ETA (`1`/`true`);
 * - `preRo` — only cases without an RO yet (`1`/`true`).
 *
 * So a reload, a shared link or the Back button restores exactly what was on
 * screen, and the case link in an arrival email (`?case=<id>`) opens the
 * inspector directly. Off means absent: false flags, a blank search and no
 * selection never appear in the URL.
 */

export type WorkspaceUrlState = Readonly<{
  view: PartsView;
  caseId: string | null;
  query: string;
  noEta: boolean;
  preRo: boolean;
}>;

export const URL_PARAM = {
  view: "view",
  case: "case",
  query: "q",
  noEta: "noEta",
  preRo: "preRo",
} as const;

function parseFlag(value: string | null): boolean {
  return value === "1" || value === "true";
}

export function parseWorkspaceUrlState(
  params: URLSearchParams,
): WorkspaceUrlState {
  const view = params.get(URL_PARAM.view);
  const caseId = params.get(URL_PARAM.case);
  return {
    view: isPartsView(view) ? view : DEFAULT_PARTS_VIEW,
    caseId: caseId ? caseId : null,
    query: (params.get(URL_PARAM.query) ?? "").trim(),
    noEta: parseFlag(params.get(URL_PARAM.noEta)),
    preRo: parseFlag(params.get(URL_PARAM.preRo)),
  };
}

/** `view` always travels (it is the address of a list); everything else only when set. */
export function serializeWorkspaceUrlState(
  state: WorkspaceUrlState,
): URLSearchParams {
  const params = new URLSearchParams();
  params.set(URL_PARAM.view, state.view);
  const query = state.query.trim();
  if (query) params.set(URL_PARAM.query, query);
  if (state.noEta) params.set(URL_PARAM.noEta, "1");
  if (state.preRo) params.set(URL_PARAM.preRo, "1");
  if (state.caseId) params.set(URL_PARAM.case, state.caseId);
  return params;
}

export interface WorkspaceUrlStateApi {
  state: WorkspaceUrlState;
  /** Shows another list. Search and filters travel along; the selection is dropped. */
  setView(view: PartsView): void;
  /** Opens a case in the inspector (`null` closes it). A history entry, so Back returns to the previous selection. */
  selectCase(caseId: string | null): void;
  /** Replaces the search; typing never litters the history. */
  setQuery(query: string): void;
  setNoEta(on: boolean): void;
  setPreRo(on: boolean): void;
}

export function useWorkspaceUrlState(): WorkspaceUrlStateApi {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(
    () => parseWorkspaceUrlState(searchParams),
    [searchParams],
  );

  // The router applies a navigation asynchronously (a transition), so two
  // writes in quick succession — the search field committing on blur and the
  // filter the click landed on — would each start from the address the last
  // render saw and the second would undo the first. Writes therefore build on
  // the last state *written* until the address has caught up with it.
  const pending = useRef<WorkspaceUrlState | null>(null);
  const inFlight = useRef<Set<string>>(new Set());

  useEffect(() => {
    const landed = searchParams.toString();
    if (inFlight.current.delete(landed)) {
      if (inFlight.current.size === 0) pending.current = null;
      return;
    }
    // Somewhere we did not write to (Back, a link): forget what was pending.
    inFlight.current.clear();
    pending.current = null;
  }, [searchParams]);

  const update = useCallback(
    (
      patch: (current: WorkspaceUrlState) => WorkspaceUrlState,
      options: { replace: boolean },
    ) => {
      const base = pending.current ?? parseWorkspaceUrlState(searchParams);
      const next = patch(base);
      const serialized = serializeWorkspaceUrlState(next);
      pending.current = next;
      inFlight.current.add(serialized.toString());
      setSearchParams(serialized, { replace: options.replace });
    },
    [searchParams, setSearchParams],
  );

  const setView = useCallback(
    (view: PartsView) =>
      update((current) => ({ ...current, view, caseId: null }), {
        replace: false,
      }),
    [update],
  );

  const selectCase = useCallback(
    (caseId: string | null) =>
      update((current) => ({ ...current, caseId }), { replace: false }),
    [update],
  );

  const setQuery = useCallback(
    (query: string) =>
      update((current) => ({ ...current, query }), { replace: true }),
    [update],
  );

  const setNoEta = useCallback(
    (noEta: boolean) =>
      update((current) => ({ ...current, noEta }), { replace: true }),
    [update],
  );

  const setPreRo = useCallback(
    (preRo: boolean) =>
      update((current) => ({ ...current, preRo }), { replace: true }),
    [update],
  );

  return useMemo(
    () => ({ state, setView, selectCase, setQuery, setNoEta, setPreRo }),
    [state, setView, selectCase, setQuery, setNoEta, setPreRo],
  );
}
